import { Trip } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { StayRow } from "../models/StayRow";
import { tripsInStay } from "../utils/stayTrips";
import { useTripsByDeviceAndDateRange } from "@/hooks/trips/useTripsByDeviceAndDateRange";

const DATE_TIME = new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "medium" });
const KM = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });

/** The trips that started during one stay, loaded only when the row is opened. */
export default function StayTripsPanel({ row }: { row: StayRow }) {
  const { t } = useTranslation();
  // Fixed per open panel, so a running stay does not refetch on every render.
  const until = useMemo(() => row.exit ?? new Date(), [row.exit]);
  const { data, isLoading, isError } = useTripsByDeviceAndDateRange({
    deviceId: row.deviceId,
    fromDate: row.entry,
    toDate: until,
    enabled: true,
  });

  const trips = useMemo(() => tripsInStay((data ?? []) as Trip[], row.entry, until), [data, row.entry, until]);

  return (
    <Box sx={{ py: 1, px: 6, bgcolor: "action.hover" }}>
      {isLoading ? (
        <CircularProgress size={20} />
      ) : isError ? (
        <Alert severity="error">{t("zoneReportPage.trips.loadFailed")}</Alert>
      ) : trips.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("zoneReportPage.trips.none")}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("zoneReportPage.trips.start")}</TableCell>
              <TableCell>{t("zoneReportPage.trips.stop")}</TableCell>
              <TableCell>{t("zoneReportPage.trips.duration")}</TableCell>
              <TableCell align="right">{t("zoneReportPage.trips.distance")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trips.map(trip => (
              <TableRow key={trip.id}>
                <TableCell>{DATE_TIME.format(new Date(trip.start))}</TableCell>
                <TableCell>{DATE_TIME.format(new Date(trip.stop))}</TableCell>
                <TableCell>{String(trip.drivingDuration ?? "").split(".")[0]}</TableCell>
                <TableCell align="right">{KM.format(trip.distance ?? 0)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
